import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Box, Button, Checkbox, FormControlLabel, Grid, InputLabel } from "@mui/material";
import { toast } from "react-toastify";
import Hoc from "../../component/HOC";
import FormInput from "../ProgramSubscriptions/FormInput";
import SingleSelect from "../../atoms/SingleSelect";
import RichTextEditor from "../ProgramSubscriptions/TextEditor";
import "../ProgramSubscriptions/Program.scss";
import {
  getOfferModal,
  getProgramList,
  saveOfferModal,
  updateOfferModal,
  showDetail,
} from "../../redux/action/user";

const decodeHtmlEntitiesToHtml = (input) => {
  const str = String(input ?? "");
  if (!str) return "";
  // Only decode if it looks entity-escaped.
  if (!str.includes("&lt;") && !str.includes("&gt;") && !str.includes("&amp;"))
    return str;
  const textarea = document.createElement("textarea");
  textarea.innerHTML = str;
  return textarea.value;
};

const normalizeOfferModal = (d) => {
  if (!d || typeof d !== "object" || Object.keys(d).length === 0) return null;
  return {
    id: d.id ?? d.ID ?? "",
    enabled: String(d.enabled ?? "0") === "1" || d.enabled === true,
    modalTitle: d.modal_title ?? d.modalTitle ?? "",
    modalContent: decodeHtmlEntitiesToHtml(d.modal_content ?? d.modalContent ?? ""),
    coupon: d.coupon ?? "",
    programId: String(d.program_id ?? d.programId ?? ""),
    modalButtonLabel: d.modal_button_label ?? d.modalButtonLabel ?? "",
  };
};

const OfferModalForm = (props) => {
  const [formData, setFormData] = useState({
    enabled: false,
    modalTitle: "",
    modalContent: "",
    coupon: "",
    programId: "",
    modalButtonLabel: "",
  });

  const programOptions = useMemo(() => {
    const rows = props.programList?.programList || [];
    const activeSubscriptions = rows.filter((p) => {
      const isActive = String(p?.status ?? "1") === "1";
      const isSubscription =
        p?.isSubscription === true ||
        String(p?.isSubscription) === "1" ||
        p?.is_subscription === true ||
        String(p?.is_subscription) === "1";
      return isActive && isSubscription;
    });
    return [
      { value: "", label: "Select subscription" },
      ...activeSubscriptions.map((p) => ({
        value: String(p.ID),
        label: p.name || `Program ${p.ID}`,
      })),
    ];
  }, [props.programList]);

  useEffect(() => {
    props.showDetail(true);
    props.getProgramList("?page=1&pageSize=500");
    props.getOfferModal();
    return () => props.showDetail(false);
  }, []);

  useEffect(() => {
    const n = normalizeOfferModal(props.offerModal);
    if (n) {
      setFormData({
        enabled: n.enabled,
        modalTitle: n.modalTitle,
        modalContent: n.modalContent,
        coupon: n.coupon,
        programId: n.programId ? String(n.programId) : "",
        modalButtonLabel: n.modalButtonLabel,
      });
    }
  }, [props.offerModal]);

  const handleFormChange = useCallback(
    (field) => (e) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleContentChange = useCallback((_, content) => {
    setFormData((prev) => ({ ...prev, modalContent: content }));
  }, []);

  const handleSubmit = async () => {
    if (!formData.modalTitle?.trim()) {
      toast.error("Modal title is required");
      return;
    }
    if (!formData.programId) {
      toast.error("Please select a subscription");
      return;
    }
    if (!formData.modalButtonLabel?.trim()) {
      toast.error("Modal button label is required");
      return;
    }

    const payload = new URLSearchParams();
    const existing = normalizeOfferModal(props.offerModal);
    payload.append("enabled", formData.enabled ? "1" : "0");
    payload.append("modal_title", formData.modalTitle || "");
    payload.append("modal_content", formData.modalContent || "");
    payload.append("coupon", formData.coupon || "");
    payload.append("program_id", formData.programId || "");
    payload.append("modal_button_label", formData.modalButtonLabel || "");

    const res =
      existing?.id
        ? await props.updateOfferModal(
            (() => {
              const p2 = new URLSearchParams(payload);
              p2.append("id", existing.id);
              return p2;
            })()
          )
        : await props.saveOfferModal(payload);
    if (res && res.status === 200) {
      if (res.data?.message) toast.success(res.data.message);
      else toast.success("Saved");
      props.getOfferModal();
    } else {
      toast.error(
        res?.response?.data?.message || res?.message || "Something went wrong"
      );
    }
  };

  return (
    <Box sx={pageWrap}>
      <Hoc activeMenu="Offer Modal" subTitle="Overview">
        <Box className="container-fluid p-0 mt-4" sx={panelWrap}>
          <Grid container spacing={2} className="FieldsWrap">
            <Grid item xs={12} md={12}>
              <Box
                sx={{
                  backgroundColor: "transparent",
                  padding: "8px",
                  display: "flex",
                  alignItems: "center",
                  borderBottom: "1px solid rgb(89 89 89 / 17%)",
                }}
              >
                <InputLabel
                  style={{
                    color: "#fff",
                    width: "13%",
                    marginRight: "8px",
                    fontSize: "0.8rem",
                    textTransform: "uppercase",
                  }}
                >
                  Enable:
                </InputLabel>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.enabled}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, enabled: e.target.checked }))
                      }
                      color="primary"
                      sx={{
                        color: "#fff",
                        "& .MuiSvgIcon-root": {
                          border: "1px solid #000",
                          borderRadius: "2px",
                        },
                      }}
                    />
                  }
                  label=""
                  sx={{ margin: 0 }}
                />
              </Box>

              <FormInput
                label="Modal Title"
                value={formData.modalTitle}
                onChange={handleFormChange("modalTitle")}
                placeholder="Enter modal title"
              />

              <Box
                sx={{
                  backgroundColor: "transparent",
                  padding: "8px",
                  borderBottom: "1px solid rgb(89 89 89 / 17%)",
                }}
              >
                <label
                  style={{
                    display: "block",
                    color: "#fff",
                    width: "13%",
                    marginBottom: "8px",
                    fontSize: "0.8rem",
                    textTransform: "uppercase",
                  }}
                >
                  Modal Content:
                </label>
                <Box sx={{ width: "100%", backgroundColor: "#fff", borderRadius: 1 }}>
                  <RichTextEditor
                    index={0}
                    value={formData.modalContent}
                    onChange={handleContentChange}
                    rawHtml
                  />
                </Box>
              </Box>

              <FormInput
                label="Coupon"
                value={formData.coupon}
                onChange={handleFormChange("coupon")}
                placeholder="Coupon code"
              />

              <Grid item xs={12} md={12}>
                <Box
                  sx={{
                    backgroundColor: "transparent",
                    padding: "8px",
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "1px solid rgb(89 89 89 / 17%)",
                  }}
                >
                  <InputLabel
                    style={{
                      color: "#fff",
                      width: "13%",
                      marginRight: "8px",
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                    }}
                  >
                    Programs:
                  </InputLabel>
                  <Box sx={{ width: "87%" }}>
                    <SingleSelect
                      label=""
                      options={programOptions}
                      value={formData.programId}
                      onChange={handleFormChange("programId")}
                      disabled={props.ProgramListLoading}
                    />
                  </Box>
                </Box>
              </Grid>

              <FormInput
                label="Modal Button label"
                value={formData.modalButtonLabel}
                onChange={handleFormChange("modalButtonLabel")}
                placeholder="e.g. Lock in offer"
              />
            </Grid>
          </Grid>

          <Box sx={{ px: 2, pb: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={props.saveOfferModalLoading || props.offerModalLoading}
              sx={{
                mt: 1,
                backgroundColor: "#0bd7ee",
                color: "#000",
                textTransform: "none",
              }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Hoc>
    </Box>
  );
};

const pageWrap = {
  ".menu-item-container": {
    "& p": {
      color: "#fff",
      opacity: 1,
    },
  },
};

const panelWrap = {
  boxShadow:
    "rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px",
  width: "100%",
  overflowX: "auto",
  backgroundColor: "rgb(15, 28, 30)",
  border: "1px solid rgb(39, 51, 52)",
  color: "rgb(255, 255, 255)",
  margin: "10px 20px",
};

const actionCreators = {
  showDetail,
  getProgramList,
  getOfferModal,
  saveOfferModal,
  updateOfferModal,
};

export default connect(({ user }) => user, actionCreators)(OfferModalForm);
